// Define the Student interface locally since it's not exported from supabase types
interface Student {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  age: number | null;
  birthdate: string | null;
  gender: string | null;
  belt: string | null;
  status: "Active" | "Inactive";
  join_date: string | null;
  last_checked_in: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  relationship_to_student: string | null;
  membership_type: string | null;
  notes: string | null;
  ai_drop_off_risk: number;
  studio_id: string;
  stripe_customer_id?: string;
  overdue_amount?: number;
  has_payment_method?: boolean;
  stripe_subscription_id?: string | string[];
  subscription_cancel_at?: string;
  subscription_status?: string;
  stripe_payment_method_id?: string;
  waiver_signed?: boolean;
  belt_last_changed?: string | null;
  student_image?: string | null;
  assigned_classes?: string[];
  membership_type_ids?: string[];
  family_id?: string | null;
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
  subscription_details?: Array<{
    id: string;
    status: string;
    membership_type_id: string;
    membership_name: string;
    current_period_start: number | null;
    current_period_end: number | null;
    calculated_billing_period_end: number | null;
    cancel_at_period_end: boolean;
    cancel_at?: number | null;
    isCanceling?: boolean;
    removed_from_billing?: boolean;
  }>;
}

export interface OptimisticUpdate {
  studentId: string;
  updates: Partial<Student>;
  timestamp: number;
}

export const createOptimisticUpdate = (
  studentId: string, 
  updates: Partial<Student>
): OptimisticUpdate => ({
  studentId,
  updates,
  timestamp: Date.now()
});

export const applyOptimisticUpdate = (
  students: Student[],
  update: OptimisticUpdate
): Student[] => {
  return students.map(student => 
    student.id === update.studentId 
      ? { ...student, ...update.updates }
      : student
  );
};

export const applyMultipleOptimisticUpdates = (
  students: Student[],
  updates: OptimisticUpdate[]
): Student[] => {
  let updatedStudents = [...students];
  
  updates.forEach(update => {
    updatedStudents = applyOptimisticUpdate(updatedStudents, update);
  });
  
  return updatedStudents;
};

export const removeStudentOptimistically = (
  students: Student[],
  studentId: string
): Student[] => {
  return students.filter(student => student.id !== studentId);
};

export const addStudentOptimistically = (
  students: Student[],
  newStudent: Student
): Student[] => {
  return [...students, newStudent];
};

// Helper to determine if an update should be optimistic
export const shouldUseOptimisticUpdate = (operation: string): boolean => {
  const optimisticOperations = [
    'student-updated',
    'payment-status-changed', 
    'membership-changed',
    'ai-risk-recalculated',
    'status-synced',
    'family-changed'
  ];
  
  return optimisticOperations.includes(operation);
};

// Helper to determine if operation needs full refresh
export const needsFullRefresh = (operation: string): boolean => {
  const fullRefreshOperations = [
    'student-deleted',
    'bulk-import',
    'student-created' // New students might affect counts/filters
  ];
  
  return fullRefreshOperations.includes(operation);
};
